import {RuntimeAdapter} from '@assistant-ui/core/react';
import {Tools, useAui} from '@assistant-ui/react';
import {AuiProvider} from '@assistant-ui/store';
import React, {useCallback, useLayoutEffect, useMemo, useRef} from 'react';
import {useLocation} from 'react-router-dom';

import {useNavContext} from '../../components/NavContextProvider';
import {isMountedInShell, useActiveProductId} from '../../shellRemote';
import {billingStore} from '../chat/billing';
import {
  type AgentChatAvailability,
  wbAgentControllerStore,
} from '../chat/controller';
import {WBAgentRuntime} from '../chat/runtime/backend';
import {useRootTurnScope} from '../chat/runtime/scope.query';
import {
  AgentChatStoreProvider,
  selectActiveThread,
  useAgentChatStore,
} from '../chat/store';
import {useAgentChatRuntime} from '../chat/useAgentChatRuntime';
import {useFallbackFromMissingThread} from '../chat/useFallbackFromMissingThread';
import {useThreadReadTracker} from '../chat/useThreadReadTracker';
import {useWbAgentClient} from '../client/useWbAgentClient';
import {useComputeWBAgentBilling} from './billing/useComputeWBAgentBilling';
import {useRefreshBillingOnOpen} from './billing/useRefreshBillingOnOpen';
import {WBAgentDataUIs} from './parts/WBAgentDataUIs';
import {wbAgentToolkit} from './tools/toolkit';
import {
  AgentChatAvailabilityContext,
  ShareTracesPolicyContext,
  useAgentChatAvailability,
  useComputeAgentChatAvailability,
  useComputeShareTracesPolicy,
} from './useAgentChatAvailability';
import {WBAgentControllerBridge} from './WBAgentControllerBridge';

const FORGE_AGENT_CONFIG_ALIAS = 'prod-forge';

/**
 * AgentChatProvider supplies the chat store and availability to its subtree. The
 * AUI runtime is mounted separately, only when available, by
 * `AgentChatRuntimeBoundary`.
 */
export function AgentChatProvider({children}: {children: React.ReactNode}) {
  const availability = useComputeAgentChatAvailability();
  const shareTracesPolicy = useComputeShareTracesPolicy();

  usePublishAgentChatAvailability(availability);
  usePublishWBAgentBilling(availability.isAvailable);

  return (
    <AgentChatAvailabilityContext.Provider value={availability}>
      <ShareTracesPolicyContext.Provider value={shareTracesPolicy}>
        <AgentChatStoreProvider>{children}</AgentChatStoreProvider>
      </ShareTracesPolicyContext.Provider>
    </AgentChatAvailabilityContext.Provider>
  );
}

/**
 * AgentChatRuntimeBoundary mounts the AUI runtime and renders its children only
 * when the feature is available. Components that call `useAui` must render inside
 * it. Must itself render inside `AgentChatProvider`.
 */
export function AgentChatRuntimeBoundary({
  children,
}: {
  children: React.ReactNode;
}) {
  const {isAvailable} = useAgentChatAvailability();
  if (!isAvailable) {
    return null;
  }

  return <AssistantRuntime>{children}</AssistantRuntime>;
}

function AssistantRuntime({children}: {children: React.ReactNode}) {
  const {params} = useNavContext();
  const {pathname: appPath} = useLocation();
  const productId = useActiveProductId();
  const promptContext = useMemo(
    () => ({...params, appPath, productId}),
    [params, appPath, productId]
  );
  const rootTurnScope = useRootTurnScope(params.entityName, params.projectName);
  const wbAgentClient = useWbAgentClient(
    rootTurnScope?.entity,
    rootTurnScope?.project
  );
  const store = useAgentChatStore();

  const getParentNodeId = useCallback(
    (nodeId: string) => {
      const thread = selectActiveThread(store.getState());
      return thread?.messageTree.parentId[nodeId] ?? null;
    },
    [store]
  );

  const agentRuntime = useMemo(
    () =>
      wbAgentClient
        ? new WBAgentRuntime(
            wbAgentClient,
            getParentNodeId,
            isMountedInShell() ? FORGE_AGENT_CONFIG_ALIAS : null
          )
        : null,
    [wbAgentClient, getParentNodeId]
  );

  const auiRuntime = useAgentChatRuntime(agentRuntime, promptContext);
  useFallbackFromMissingThread();
  useThreadReadTracker(store);
  useRefreshBillingOnOpen();
  const aui = useAui({
    threads: RuntimeAdapter(auiRuntime),
    tools: Tools({toolkit: wbAgentToolkit}),
  });

  return (
    <AuiProvider value={aui}>
      <WBAgentControllerBridge />
      <WBAgentDataUIs />
      {children}
    </AuiProvider>
  );
}

/**
 * usePublishAgentChatAvailability publishes the computed availability into the
 * controller store, so buttons anywhere in the app can read it; the value is
 * otherwise local to this subtree.
 */
function usePublishAgentChatAvailability(
  availability: AgentChatAvailability
): void {
  // The layout effect runs before paint, so external buttons don't show a
  // stale-enabled frame after the feature turns off.
  useLayoutEffect(
    () => wbAgentControllerStore.registerAvailability(availability),
    [availability]
  );
}

// Whether WB Agent billing enforcement is live. While false, the store is fed
// `unenforced` so every gate stays open and the enforcement machinery ships
// inert.
const WB_AGENT_BILLING_ENABLED: boolean = false;

// Full-page and drawer providers can briefly overlap during route transitions.
// Only the most recent publisher may reset their shared billing snapshot.
let activeBillingPublisher: object | null = null;

/**
 * usePublishWBAgentBilling derives the WB Agent billing snapshot and publishes
 * it into the module-singleton store, so consumers outside this subtree can
 * gate on access.
 */
function usePublishWBAgentBilling(enabled: boolean): void {
  const {billing, refresh} = useComputeWBAgentBilling(enabled);
  const publisherRef = useRef({});

  // The layout effects run before paint so an out-of-tree gate never shows a
  // stale frame.
  useLayoutEffect(() => {
    const publisher = publisherRef.current;
    activeBillingPublisher = publisher;
    billingStore.setBilling(
      WB_AGENT_BILLING_ENABLED ? billing : {status: 'unenforced'}
    );

    return () => {
      if (activeBillingPublisher === publisher) {
        activeBillingPublisher = null;
        billingStore.reset();
      }
    };
  }, [billing]);

  useLayoutEffect(() => billingStore.registerRefresh(refresh), [refresh]);
}
